import type React from 'react';

import { Checkbox, Grid, Typography, FormHelperText, useTheme } from '@mui/material';
import { ResourceReadFields } from '@ragu-components/ord-types';
import { useGetList, useInput, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import type { CheckboxProps } from '@mui/material';
import type { AclResource, ResourceRead } from '@ragu-components/ord-types';
import type { InputProps } from 'react-admin';

/** Table header for AclResourcesInput */
const AclResourcesHeader = (): React.ReactElement => {
  const translate = useTranslate();
  return (
    <Grid container>
      <Grid size='grow'>
        <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
          {translate('ord.permissions.resource')}
        </Typography>
      </Grid>
      <Grid size='grow' sx={{ textAlign: 'center' }}>
        <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
          {translate('ord.permissions.create')}
        </Typography>
      </Grid>
      <Grid size='grow' sx={{ textAlign: 'center' }}>
        <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
          {translate('ord.permissions.list')}
        </Typography>
      </Grid>
    </Grid>
  );
};

/** Add acl to aclValues */
const addAcl = (acl: AclResource, aclValues: AclResource[]): AclResource[] => {
  return [acl, ...aclValues];
};

/** Remove acl from aclValues */
const rmAcl = (acl: AclResource, aclValues: AclResource[]): AclResource[] => {
  return aclValues.filter((aclValue) => aclValue.resource_id !== acl.resource_id || aclValue.type !== acl.type);
};

/** Returns true if AclValues contains acl */
const getAcl = (acl: AclResource, aclValues: AclResource[]): boolean => {
  return (
    aclValues.filter((aclValue) => aclValue.resource_id === acl.resource_id && aclValue.type === acl.type).length > 0
  );
};

/** Convert a snake_case resource name to Title Case as a last-resort display fallback.
 * e.g. "ap_authentication" → "Ap Authentication" */
const resourceNameFallback = (name: string): string => name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** List all resources and display/edit the permission associated with it */
export const AclResourcesInput = (props: InputProps): React.ReactElement => {
  const translate = useTranslate();
  const theme = useTheme();
  const { data } = useGetList<Required<ResourceRead>>('resources', {
    filter: { meta: { pageSize: 1000 } },
    sort: { field: ResourceReadFields.name, order: 'ASC' },
  });
  const {
    field: { onChange: inputOnChange, onBlur, value, name: fieldName },
  } = useInput({ ...props });
  const { disabled } = props;

  // Get field validation state for error highlighting
  const { getFieldState } = useFormContext();
  const fieldState = getFieldState(fieldName);
  const hasError = fieldState.invalid;
  const errorMessage = fieldState.error?.message;
  if (!data) return <></>;

  const allData = data.find(({ name }) => name === 'all') ?? { id: 'all', name: 'all' };
  const getDisplayName = (name: string): string =>
    translate(`ord.permissions.${name}`, {
      _: translate(`resources.${name}.name`, { _: resourceNameFallback(name) }),
    });
  const resourceData = data
    .filter(({ name }) => name !== 'all')
    .sort((a, b) => getDisplayName(a.name).localeCompare(getDisplayName(b.name)));
  const aclValues = (Array.isArray(value) ? value : []) as AclResource[];

  const onChangeHandler = (acl: AclResource) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      const checked = event.target.checked;
      if (checked) inputOnChange(addAcl(acl, aclValues));
      else inputOnChange(rmAcl(acl, aclValues));
    };
  };

  const generateProps = (acl: AclResource): CheckboxProps => ({
    name: `${fieldName}.${acl.resource_id}.${acl.type}`,
    disabled:
      disabled === true ||
      (acl.resource_id !== allData.id && getAcl({ resource_id: allData.id, type: acl.type }, aclValues)),
    checked: getAcl({ resource_id: allData.id, type: acl.type }, aclValues) || getAcl(acl, aclValues),
    onChange: onChangeHandler(acl),
    onBlur,
    sx: {
      padding: 0,
      color: hasError ? 'error.main' : undefined,
      '&.Mui-checked': {
        color: hasError ? 'error.main' : undefined,
      },
    },
  });

  return (
    <Grid>
      <AclResourcesHeader />
      <Grid container key={allData.id}>
        <Grid size='grow'>
          <Typography variant='body2' color={hasError ? 'error.main' : 'inherit'}>
            {translate(`ord.permissions.all`, { _: 'All' })}
          </Typography>
        </Grid>
        <Grid size='grow' sx={{ textAlign: 'center' }}>
          <Checkbox {...generateProps({ resource_id: allData.id, type: 'create' })} />
        </Grid>
        <Grid size='grow' sx={{ textAlign: 'center' }}>
          <Checkbox {...generateProps({ resource_id: allData.id, type: 'list' })} />
        </Grid>
      </Grid>
      {resourceData.map(({ id, name }, index) => (
        <Grid
          container
          key={id}
          sx={{
            backgroundColor: index % 2 === 0 ? theme.palette.action.hover : 'transparent',
          }}
        >
          <Grid size='grow'>
            <Typography variant='body2' color={hasError ? 'error.main' : 'inherit'}>
              {getDisplayName(name)}
            </Typography>
          </Grid>
          <Grid size='grow' sx={{ textAlign: 'center' }}>
            <Checkbox {...generateProps({ resource_id: id, type: 'create' })} />
          </Grid>
          <Grid size='grow' sx={{ textAlign: 'center' }}>
            <Checkbox {...generateProps({ resource_id: id, type: 'list' })} />
          </Grid>
        </Grid>
      ))}
      {hasError && Boolean(errorMessage) && (
        <Grid container>
          <Grid size={12}>
            <FormHelperText error={hasError} sx={{ mt: 1 }}>
              {errorMessage}
            </FormHelperText>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
